import type { BotSchedule, OperatorAccount, Project, Site } from './types.js';

type LegalDocumentType = 'personal-data' | 'privacy-policy';

interface ProjectAdminHandlers {
  verify: (id: string) => void;
  configure: (id: string) => void;
  setGlobalTexts: (id: string, enabled: boolean) => Promise<boolean>;
  saveProject: (id: string, input: { name: string; webhookUrl: string }) => void;
  uploadLegalDocument: (id: string, type: LegalDocumentType, file: File) => Promise<boolean>;
  deleteLegalDocument: (id: string, type: LegalDocumentType) => Promise<boolean>;
  copyEmbed: (code: string) => void;
}

const siteList = document.getElementById('site-admin-list') as HTMLElement;
const operatorList = document.getElementById('operator-admin-list') as HTMLElement;
const operatorForm = document.getElementById('operator-form') as HTMLFormElement;
const projectAssignmentList = document.getElementById('operator-project-choices') as HTMLElement;
const assignmentList = document.getElementById('operator-site-choices') as HTMLElement;
const operatorTitle = document.getElementById('operator-form-title') as HTMLElement;
const operatorSubmit = document.getElementById('operator-submit') as HTMLButtonElement;
const operatorCancel = document.getElementById('operator-cancel') as HTMLButtonElement;
const accountRole = document.getElementById('account-role') as HTMLSelectElement;
const humanFields = document.getElementById('human-account-fields') as HTMLElement;
const botFields = document.getElementById('bot-account-fields') as HTMLElement;
const setupResult = document.getElementById('bot-setup-result') as HTMLElement;
const setupUrl = document.getElementById('bot-webhook-url') as HTMLElement;
const setupSecret = document.getElementById('bot-webhook-secret') as HTMLElement;

export function renderProjectAdmin(
  projects: Project[],
  sites: Site[],
  handlers: ProjectAdminHandlers
): void {
  siteList.replaceChildren();
  if (projects.length === 0) {
    siteList.append(emptyState('Создайте первый проект, чтобы получить единый код подключения.'));
    return;
  }
  for (const project of projects) {
    const card = document.createElement('article');
    card.className = 'admin-card project-admin-card';
    const top = document.createElement('div');
    top.className = 'admin-card__top';
    const copy = document.createElement('div');
    const name = document.createElement('h3');
    name.textContent = project.name;
    const count = document.createElement('p');
    const projectSites = sites.filter((site) => site.projectId === project.id);
    count.textContent = `${projectSites.length} ${pluralChats(projectSites.length)}`;
    copy.append(name, count);
    const badge = document.createElement('span');
    badge.className = `verification-badge ${project.webhookLastError ? 'verification-badge--error' : 'verification-badge--verified'}`;
    badge.textContent = project.webhookLastError ? 'Webhook: ошибка' : project.webhookUrl ? 'Webhook включён' : 'Webhook выключен';
    top.append(copy, badge);

    const embed = `<script src="${location.origin}/widget.js" data-project-id="${project.publicKey}"><\/script>`;
    const key = document.createElement('div');
    key.className = 'project-embed';
    const code = document.createElement('code');
    code.textContent = embed;
    key.append(code, actionButton('Копировать код', () => handlers.copyEmbed(embed), true));

    const projectForm = document.createElement('form');
    projectForm.className = 'project-settings-form';
    const nameInput = document.createElement('input');
    nameInput.name = 'name';
    nameInput.value = project.name;
    nameInput.maxLength = 120;
    nameInput.required = true;
    nameInput.setAttribute('aria-label', 'Название проекта');
    const webhookInput = document.createElement('input');
    webhookInput.name = 'webhookUrl';
    webhookInput.type = 'url';
    webhookInput.placeholder = 'https://example.ru/webhook';
    webhookInput.value = project.webhookUrl ?? '';
    webhookInput.setAttribute('aria-label', 'URL webhook');
    const save = actionButton('Сохранить проект', () => undefined, false);
    save.type = 'submit';
    projectForm.append(nameInput, webhookInput, save);
    projectForm.addEventListener('submit', (event) => {
      event.preventDefault();
      handlers.saveProject(project.id, { name: nameInput.value.trim(), webhookUrl: webhookInput.value.trim() });
    });

    const legalDocuments = renderLegalDocuments(project, handlers);

    const chats = document.createElement('div');
    chats.className = 'project-chat-list';
    for (const site of projectSites) chats.append(renderChatCard(site, handlers));
    if (projectSites.length === 0) chats.append(emptyState('В проекте пока нет чатов. Добавьте домен формой слева.'));
    card.append(top, key, projectForm, legalDocuments, chats);
    siteList.append(card);
  }
  const legacySites = sites.filter((site) => !site.projectId);
  if (legacySites.length > 0) {
    const legacy = document.createElement('article');
    legacy.className = 'admin-card project-admin-card project-admin-card--legacy';
    const heading = document.createElement('div');
    heading.className = 'admin-card__top';
    const copy = document.createElement('div');
    const title = document.createElement('h3');
    title.textContent = 'Без проекта';
    const note = document.createElement('p');
    note.textContent = 'Старые чаты сохраняют свои ключи. Назначьте им проект в настройках чата.';
    copy.append(title, note);
    heading.append(copy);
    const chats = document.createElement('div');
    chats.className = 'project-chat-list';
    for (const site of legacySites) chats.append(renderChatCard(site, handlers));
    legacy.append(heading, chats);
    siteList.append(legacy);
  }
}

function renderLegalDocuments(project: Project, handlers: ProjectAdminHandlers): HTMLElement {
  const ready = Boolean(project.legalDocuments.personalData && project.legalDocuments.privacyPolicy);
  const panel = document.createElement('section');
  panel.className = `legal-documents${ready ? ' is-ready' : ''}`;
  const heading = document.createElement('div');
  heading.className = 'legal-documents__heading';
  const copy = document.createElement('div');
  const kicker = document.createElement('p');
  kicker.textContent = 'LEGAL / PROJECT';
  const title = document.createElement('h4');
  title.textContent = 'Юридические документы';
  copy.append(kicker, title);
  const state = document.createElement('span');
  state.textContent = ready ? 'Согласие включено' : 'Нужно 2 документа';
  heading.append(copy, state);
  const note = document.createElement('p');
  note.className = 'legal-documents__note';
  note.textContent = ready
    ? 'Ссылки уже показываются в формах всех чатов этого проекта.'
    : 'После загрузки обоих PDF в формах появится обязательный чекбокс согласия.';
  const list = document.createElement('div');
  list.className = 'legal-document-list';
  list.append(
    renderLegalDocumentRow(project, 'personal-data', 'Обработка персональных данных', handlers),
    renderLegalDocumentRow(project, 'privacy-policy', 'Политика конфиденциальности', handlers)
  );
  panel.append(heading, note, list);
  return panel;
}

function renderLegalDocumentRow(
  project: Project,
  type: LegalDocumentType,
  label: string,
  handlers: ProjectAdminHandlers
): HTMLElement {
  const legalDocument = type === 'personal-data'
    ? project.legalDocuments.personalData
    : project.legalDocuments.privacyPolicy;
  const row = document.createElement('div');
  row.className = `legal-document-row${legalDocument ? ' has-file' : ''}`;
  const icon = document.createElement('span');
  icon.className = 'legal-document-row__icon';
  icon.textContent = 'PDF';
  const details = document.createElement('div');
  details.className = 'legal-document-row__details';
  const name = document.createElement('strong');
  name.textContent = label;
  const meta = document.createElement('span');
  meta.textContent = legalDocument
    ? `${legalDocument.fileName} · ${formatFileSize(legalDocument.sizeBytes)} · ${formatDocumentDate(legalDocument.uploadedAt)}`
    : 'Файл ещё не загружен';
  details.append(name, meta);
  const actions = document.createElement('div');
  actions.className = 'legal-document-row__actions';
  if (legalDocument) {
    const externalUrl = new URL(legalDocument.url, location.origin).href;
    const open = document.createElement('a');
    open.href = externalUrl;
    open.target = '_blank';
    open.rel = 'noopener noreferrer';
    open.textContent = 'Открыть';
    const copyLink = actionButton('Ссылка', () => {
      void navigator.clipboard.writeText(externalUrl).then(() => {
        copyLink.textContent = 'Скопировано';
        window.setTimeout(() => { copyLink.textContent = 'Ссылка'; }, 1_500);
      });
    }, true);
    actions.append(open, copyLink);
  }
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'application/pdf,.pdf';
  input.className = 'is-hidden';
  const upload = actionButton(legalDocument ? 'Заменить' : 'Загрузить PDF', () => input.click(), !legalDocument);
  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    if (!file) return;
    upload.disabled = true;
    upload.textContent = 'Загрузка…';
    const saved = await handlers.uploadLegalDocument(project.id, type, file);
    if (!saved) {
      upload.disabled = false;
      upload.textContent = legalDocument ? 'Заменить' : 'Загрузить PDF';
      input.value = '';
    }
  });
  actions.append(input, upload);
  if (legalDocument) {
    actions.append(actionButton('Удалить', () => {
      if (window.confirm(`Удалить документ «${label}»? Ссылка перестанет открываться.`)) {
        void handlers.deleteLegalDocument(project.id, type);
      }
    }, true, true));
  }
  row.append(icon, details, actions);
  return row;
}

function formatFileSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} МБ`
    : `${Math.max(1, Math.round(bytes / 1024))} КБ`;
}

function formatDocumentDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    .format(new Date(value));
}

export function renderOperatorAdmin(
  operators: OperatorAccount[],
  projects: Project[],
  sites: Site[],
  handlers: {
    edit: (operator: OperatorAccount) => void;
    rotateSecret: (operator: OperatorAccount) => void;
    delete: (operator: OperatorAccount) => void;
  }
): void {
  operatorList.replaceChildren();
  for (const operator of operators) {
    const card = document.createElement('article');
    card.className = `admin-card operator-card${operator.role === 'bot' ? ' operator-card--bot' : ''}${operator.enabled ? '' : ' is-disabled'}`;
    const identity = document.createElement('div');
    identity.className = 'operator-card__identity';
    const avatar = document.createElement('span');
    avatar.textContent = operator.role === 'bot' ? 'AI' : operator.displayName.charAt(0).toUpperCase() || 'О';
    const copy = document.createElement('div');
    const name = document.createElement('h3');
    name.textContent = operator.displayName;
    const email = document.createElement('p');
    email.textContent = operator.role === 'bot'
      ? `Suvvy · ${operator.suvvyIntegration?.tokenConfigured ? 'токен подключён' : 'нужен токен канала'}`
      : operator.email ?? '';
    copy.append(name, email);
    const role = document.createElement('span');
    role.className = `role-badge${operator.role === 'bot' ? ' role-badge--bot' : ''}`;
    role.textContent = operator.role === 'admin' ? 'Администратор' : operator.role === 'bot' ? 'Бот' : 'Оператор';
    identity.append(avatar, copy, role);

    const assigned = document.createElement('div');
    assigned.className = 'operator-sites';
    const assignedSites = operator.role === 'admin'
      ? ['Все проекты и чаты']
      : [
          ...operator.projectIds.map((id) => projects.find((project) => project.id === id)?.name)
            .filter(Boolean).map((name) => `Проект: ${name}`),
          ...operator.siteIds.map((id) => sites.find((site) => site.id === id)?.name)
            .filter(Boolean).map((name) => `Чат: ${name}`)
        ] as string[];
    for (const siteName of assignedSites.length ? assignedSites : ['Нет назначений']) {
      const chip = document.createElement('span');
      chip.textContent = siteName;
      assigned.append(chip);
    }
    card.append(identity);
    if (operator.role === 'bot' && operator.suvvyIntegration) {
      const integration = operator.suvvyIntegration;
      const status = document.createElement('div');
      status.className = `bot-status${integration.lastError ? ' bot-status--error' : ''}`;
      const summary = document.createElement('strong');
      summary.textContent = integration.lastError
        ? `Ошибка доставки: ${integration.lastError}`
        : integration.tokenConfigured ? 'Канал готов к обмену сообщениями' : 'Добавьте токен канала Suvvy';
      const webhook = document.createElement('code');
      webhook.textContent = integration.webhookUrl;
      const activity = document.createElement('span');
      activity.textContent = activityText(integration.lastInboundAt, integration.lastOutboundAt);
      status.append(summary, webhook, activity);
      const schedule = document.createElement('span');
      schedule.className = 'bot-status__schedule';
      schedule.textContent = `${scheduleText(integration.schedule)} · переопределений: ${integration.scopeSchedules.length}`;
      status.append(schedule);
      card.append(status);
    }
    card.append(assigned);
    if (operator.role === 'operator') {
      const actions = document.createElement('div');
      actions.className = 'admin-card__actions';
      actions.append(
        actionButton('Изменить доступ', () => handlers.edit(operator), false),
        actionButton('Удалить аккаунт', () => handlers.delete(operator), true, true)
      );
      card.append(actions);
    }
    if (operator.role === 'bot') {
      const actions = document.createElement('div');
      actions.className = 'admin-card__actions';
      actions.append(
        actionButton('Настроить бота', () => handlers.edit(operator), false),
        actionButton('Сменить webhook-секрет', () => handlers.rotateSecret(operator), true),
        actionButton('Удалить бота', () => handlers.delete(operator), true, true)
      );
      card.append(actions);
    }
    operatorList.append(card);
  }
}

export function prepareNewOperator(projects: Project[], sites: Site[]): void {
  operatorForm.reset();
  (operatorForm.elements.namedItem('operatorId') as HTMLInputElement).value = '';
  (operatorForm.elements.namedItem('enabled') as HTMLInputElement).checked = true;
  accountRole.disabled = false;
  accountRole.value = 'operator';
  (operatorForm.elements.namedItem('password') as HTMLInputElement).required = true;
  operatorTitle.textContent = 'Новый оператор';
  operatorSubmit.textContent = 'Создать аккаунт';
  operatorCancel.classList.add('is-hidden');
  renderAssignments(projects, sites, [], []);
  syncAccountRole();
}

export function prepareOperatorEdit(operator: OperatorAccount, projects: Project[], sites: Site[]): void {
  (operatorForm.elements.namedItem('operatorId') as HTMLInputElement).value = operator.id;
  accountRole.value = operator.role;
  accountRole.disabled = true;
  setValue('displayName', operator.displayName);
  setValue('email', operator.email ?? '');
  setValue('password', '');
  setValue('channelToken', '');
  (operatorForm.elements.namedItem('password') as HTMLInputElement).required = false;
  (operatorForm.elements.namedItem('enabled') as HTMLInputElement).checked = operator.enabled;
  operatorTitle.textContent = operator.role === 'bot' ? 'Настройка ИИ-бота' : 'Доступ оператора';
  operatorSubmit.textContent = 'Сохранить доступ';
  operatorCancel.classList.remove('is-hidden');
  renderAssignments(projects, sites, operator.projectIds, operator.siteIds);
  syncAccountRole();
  operatorForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function readOperatorForm() {
  const data = new FormData(operatorForm);
  return {
    id: String(data.get('operatorId') ?? ''),
    displayName: String(data.get('displayName') ?? '').trim(),
    email: String(data.get('email') ?? '').trim(),
    password: String(data.get('password') ?? ''),
    role: accountRole.value as 'operator' | 'bot',
    channelToken: String(data.get('channelToken') ?? '').trim(),
    enabled: (operatorForm.elements.namedItem('enabled') as HTMLInputElement).checked,
    projectIds: Array.from(projectAssignmentList.querySelectorAll<HTMLInputElement>('input:checked'))
      .map((input) => input.value),
    siteIds: Array.from(assignmentList.querySelectorAll<HTMLInputElement>('input:checked')).map((input) => input.value)
  };
}

export function bindAccountRole(): void {
  accountRole.addEventListener('change', syncAccountRole);
  document.getElementById('bot-setup-close')?.addEventListener('click', hideBotSetup);
  document.querySelectorAll<HTMLButtonElement>('[data-copy-bot]').forEach((button) => {
    button.addEventListener('click', () => {
      const value = button.dataset.copyBot === 'url' ? setupUrl.textContent : setupSecret.textContent;
      if (value) void navigator.clipboard.writeText(value);
      button.textContent = 'Скопировано';
      window.setTimeout(() => { button.textContent = 'Копировать'; }, 1_500);
    });
  });
}

export function showBotSetup(setup: { webhookUrl: string; webhookSecret: string }): void {
  setupUrl.textContent = setup.webhookUrl;
  setupSecret.textContent = setup.webhookSecret;
  setupResult.classList.remove('is-hidden');
  setupResult.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

export function hideBotSetup(): void {
  setupUrl.textContent = '';
  setupSecret.textContent = '';
  setupResult.classList.add('is-hidden');
}

export function operatorFormElement(): HTMLFormElement {
  return operatorForm;
}

export function operatorCancelElement(): HTMLButtonElement {
  return operatorCancel;
}

function renderAssignments(
  projects: Project[],
  sites: Site[],
  selectedProjectIds: string[],
  selectedSiteIds: string[]
): void {
  projectAssignmentList.replaceChildren();
  assignmentList.replaceChildren();
  for (const project of projects) {
    projectAssignmentList.append(assignmentChoice(
      project.id,
      project.name,
      'Все текущие и будущие чаты проекта',
      selectedProjectIds.includes(project.id)
    ));
  }
  if (sites.length === 0) {
    assignmentList.append(emptyState('В проектах пока нет чатов.'));
    return;
  }
  for (const site of sites) {
    assignmentList.append(assignmentChoice(
      site.id,
      site.name,
      `${site.projectName ?? 'Без проекта'} · ${site.domain ?? site.publicKey}`,
      selectedSiteIds.includes(site.id)
    ));
  }
}

function assignmentChoice(value: string, title: string, note: string, checked: boolean): HTMLLabelElement {
  const label = document.createElement('label');
  label.className = 'site-choice';
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.value = value;
  input.checked = checked;
  const copy = document.createElement('span');
  const strong = document.createElement('strong');
  strong.textContent = title;
  const small = document.createElement('small');
  small.textContent = note;
  copy.append(strong, small);
  label.append(input, copy);
  return label;
}

function renderChatCard(
  site: Site,
  handlers: {
    verify: (id: string) => void;
    configure: (id: string) => void;
    setGlobalTexts: (id: string, enabled: boolean) => Promise<boolean>;
  }
): HTMLElement {
  const card = document.createElement('article');
  card.className = 'project-chat-card';
  const copy = document.createElement('div');
  const name = document.createElement('strong');
  name.textContent = site.name;
  const url = document.createElement('a');
  url.textContent = site.domain ?? site.websiteUrl ?? 'URL не указан';
  if (site.websiteUrl) {
    url.href = site.websiteUrl;
    url.target = '_blank';
    url.rel = 'noreferrer';
  }
  copy.append(name, url);
  const badge = document.createElement('span');
  badge.className = `verification-badge verification-badge--${site.verificationStatus.toLowerCase()}`;
  badge.textContent = verificationLabel(site.verificationStatus);
  const globalToggle = document.createElement('label');
  globalToggle.className = `global-site-toggle${site.useGlobalWidgetTexts ? ' is-active' : ''}`;
  globalToggle.title = 'Использовать глобальные заголовок, название компании и приветствие';
  const globalInput = document.createElement('input');
  globalInput.type = 'checkbox';
  globalInput.checked = site.useGlobalWidgetTexts;
  globalInput.setAttribute('aria-label', `Глобальные тексты для «${site.name}»`);
  const globalLabel = document.createElement('span');
  globalLabel.textContent = 'GLOBAL';
  globalToggle.append(globalInput, globalLabel);
  globalInput.addEventListener('change', async () => {
    const enabled = globalInput.checked;
    globalInput.disabled = true;
    globalToggle.classList.toggle('is-active', enabled);
    const saved = await handlers.setGlobalTexts(site.id, enabled);
    if (!saved) {
      globalInput.checked = !enabled;
      globalToggle.classList.toggle('is-active', !enabled);
      globalInput.disabled = false;
    }
  });
  const actions = document.createElement('div');
  actions.className = 'admin-card__actions';
  actions.append(
    actionButton('Проверить', () => handlers.verify(site.id), true),
    actionButton('Настроить', () => handlers.configure(site.id), false)
  );
  card.append(copy, badge, globalToggle, actions);
  return card;
}

function pluralChats(value: number): string {
  const tail = value % 100;
  if (tail >= 11 && tail <= 14) return 'чатов';
  if (value % 10 === 1) return 'чат';
  if (value % 10 >= 2 && value % 10 <= 4) return 'чата';
  return 'чатов';
}

function actionButton(label: string, handler: () => void, secondary: boolean, danger = false): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `admin-action${secondary ? ' admin-action--secondary' : ''}${danger ? ' admin-action--danger' : ''}`;
  button.textContent = label;
  button.addEventListener('click', handler);
  return button;
}

function emptyState(text: string): HTMLElement {
  const node = document.createElement('p');
  node.className = 'admin-empty';
  node.textContent = text;
  return node;
}

function setValue(name: string, value: string): void {
  (operatorForm.elements.namedItem(name) as HTMLInputElement).value = value;
}

function syncAccountRole(): void {
  const bot = accountRole.value === 'bot';
  humanFields.classList.toggle('is-hidden', bot);
  botFields.classList.toggle('is-hidden', !bot);
  const email = operatorForm.elements.namedItem('email') as HTMLInputElement;
  const password = operatorForm.elements.namedItem('password') as HTMLInputElement;
  email.required = !bot;
  password.required = !bot && !String((operatorForm.elements.namedItem('operatorId') as HTMLInputElement).value);
  (document.getElementById('enabled-account-help') as HTMLElement).textContent = bot
    ? 'Отключённый бот не получает сообщения и не отвечает'
    : 'Отключённый оператор не сможет войти';
  operatorTitle.textContent = bot
    ? ((operatorForm.elements.namedItem('operatorId') as HTMLInputElement).value ? 'Настройка ИИ-бота' : 'Новый ИИ-бот')
    : ((operatorForm.elements.namedItem('operatorId') as HTMLInputElement).value ? 'Доступ оператора' : 'Новый оператор');
  operatorSubmit.textContent = (operatorForm.elements.namedItem('operatorId') as HTMLInputElement).value
    ? 'Сохранить доступ'
    : bot ? 'Создать бота' : 'Создать аккаунт';
}

function scheduleText(schedule: BotSchedule): string {
  if (schedule.mode === 'always') return 'Базовое расписание: круглосуточно';
  const window = (label: string, value: BotSchedule['weekdays']) => value.enabled
    ? `${label} ${value.start}–${value.end}`
    : `${label} выключены`;
  return `Базовое расписание: ${window('будни', schedule.weekdays)} · ${window('выходные', schedule.weekends)} · ${schedule.timezone}`;
}

function activityText(inbound: string | null, outbound: string | null): string {
  if (!inbound && !outbound) return 'Обмена сообщениями ещё не было';
  const parts: string[] = [];
  if (outbound) parts.push(`отправка ${formatDate(outbound)}`);
  if (inbound) parts.push(`ответ ${formatDate(inbound)}`);
  return `Последняя активность: ${parts.join(' · ')}`;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

function verificationLabel(status: Site['verificationStatus']): string {
  return {
    PENDING: 'Не проверен',
    VERIFIED: 'Установлен',
    MISSING: 'Скрипт не найден',
    ERROR: 'Ошибка проверки'
  }[status];
}
